/**
 * /el/guides/posa-atoma-se-kathe-trapezi-gamou
 *
 * The Greek counterpart of `how-many-people-wedding-table.en.js` — the capacity
 * question in the Greek seating cluster. An ADAPTATION:
 *
 *  - **Centimetres only.** The English article carries every round-table size
 *    twice, in centimetres and inches, because its readers measure in both. A
 *    Greek couple asking a κτήμα about its tables will be told 150 or 180, and
 *    a parenthetical "60 in" is noise at best. The inch figures are gone from
 *    the body AND from the FAQ, where two of the five questions were literally
 *    "how many fit at a 60-inch table" — asked here as «ροτόντα 150 εκ.».
 *
 *  - **Ροτόντες** as the default, which they are at a Greek δεξίωση, with the
 *    long/rectangular case kept as the second half of the article rather than
 *    an equal alternative.
 *
 *  - **The venue is the authority**, stated in Greek terms: ο χώρος, το κτήμα,
 *    η εταιρεία ενοικίασης. This is the article's whole argument and it is not
 *    softened in translation.
 *
 * ── The claims this guide is allowed to make ──────────────────────────────
 * Exactly one, inherited verbatim from the English module:
 *
 *   "Τα εργαλεία για τα τραπέζια είναι διαθέσιμα στο πακέτο Heirloom."
 *
 * It appears once, in the closing section, beside the link to /wedding-seating.
 * This guide describes NO product behaviour of its own — not what the seating
 * tools do, not how they work. The four prohibitions that govern the Greek
 * seating-plan guide apply here too: no drag-and-drop, no κάτοψη or visual
 * floor plan, no automatic table generation, no seating algorithm. The Greek
 * free tools this cluster competes with advertise all four; matching them is
 * the specific temptation this note exists to refuse.
 *
 * ── The numbers ───────────────────────────────────────────────────────────
 * Every figure in section 3 is a planning range, framed as one both before and
 * after the list, and paired: «άνετη αφετηρία» beside «πιθανό μέγιστο». That
 * pair is the point of the article. Neither the copy nor a later edit may
 * present a figure as an industry standard — there is none — and the venue's
 * own specification always takes priority.
 */

const howManyPeopleWeddingTableEl = {
  slug: "posa-atoma-se-kathe-trapezi-gamou",

  title: "Πόσα άτομα κάθονται σε κάθε τραπέζι στον γάμο;",
  metaTitle: "Πόσα άτομα κάθονται σε κάθε τραπέζι στον γάμο; | Agapaé",
  metaDescription:
    "Πόσοι καλεσμένοι κάθονται άνετα σε κάθε τραπέζι της δεξίωσης: μεγέθη ροτόντας, καρέκλες, σερβίτσιο, τρόπος σερβιρίσματος και πόσα τραπέζια θα χρειαστείτε τελικά.",

  eyebrow: "Τραπέζια γάμου",

  hero: {
    src: "/assets/images/moments/moment-3.jpg",
    // Describes the photograph, not the keyword — the same rule and the same
    // photograph as the English module.
    alt: "Ασπρόμαυρη φωτογραφία νύφης και γαμπρού που στέκονται κοντά ο ένας στον άλλον σε ένα ηλιόλουστο λιβάδι, με τα μέτωπά τους σχεδόν να ακουμπούν καθώς κρατιούνται χέρι χέρι",
    width: 6983,
    height: 3436,
  },

  intro: [
    `Δεν υπάρχει ένας αριθμός που να ταιριάζει σε κάθε τραπέζι γάμου.`,
    `Ένα τραπέζι μπορεί να χωράει συγκεκριμένες καρέκλες χωρίς αυτό να σημαίνει ότι είναι άνετο για ένα ολόκληρο γεύμα. Ο σωστός αριθμός εξαρτάται από τις διαστάσεις και το σχήμα του, από τις καρέκλες, το σερβίτσιο, τον τρόπο που θα σερβιριστεί το φαγητό και τον χώρο που χρειάζεται ο καθένας για να κάτσει, να φάει και να μιλήσει άνετα.`,
    `Στις περισσότερες δεξιώσεις τα οκτώ με δέκα άτομα ανά ροτόντα είναι μια χρήσιμη αφετηρία. Αφετηρία όμως, όχι κανόνας.`,
    `Πριν αποφασίσετε πόσοι θα καθίσουν σε κάθε τραπέζι, δείτε τι τραπέζια δίνει πραγματικά ο χώρος σας και πώς θα στηθεί η αίθουσα.`,
  ],

  sections: [
    {
      heading: "1. Ξεκινήστε από τα τραπέζια που δίνει ο χώρος σας",
      content: [
        `Η πρώτη ερώτηση δεν είναι πόσοι πρέπει να κάθονται σε ένα τραπέζι γάμου.`,
        `Είναι:`,
        `«Τι τραπέζια θα έχουμε τελικά;»`,
        `Κάθε κτήμα και κάθε εταιρεία ενοικίασης δουλεύει με δικά της μεγέθη και σχήματα. Η διάμετρος μιας ροτόντας αλλάζει αισθητά το πόσοι χωρούν γύρω της με άνεση, ενώ τα μακρόστενα τραπέζια λειτουργούν με εντελώς άλλη λογική.`,
        `Αν ο χώρος σάς έχει ήδη πει τις διαστάσεις, ξεκινήστε από αυτές και όχι από έναν γενικό αριθμό που βρήκατε κάπου.`,
        `Ρωτήστε και για τις καρέκλες. Το πλάτος τους, το σχήμα τους και το αν έχουν μπράτσα αλλάζουν τον χώρο που μένει τελικά στον καθένα.`,
      ],
    },
    {
      heading: "2. Άνετα δεν σημαίνει όσοι χωρούν",
      content: [
        `Το πιο συνηθισμένο λάθος στα τραπέζια του γάμου είναι να μπερδεύεται το πόσοι χωρούν με το πόσοι πρέπει να καθίσουν.`,
        `Η μέγιστη χωρητικότητα λέει τι χωράει τεχνικά.`,
        `Η άνετη χωρητικότητα ρωτά αν μένει αρκετός χώρος για:`,
        {
          type: "list",
          items: [
            `το σερβίτσιο του καθενός`,
            `τα ποτήρια και τα μαχαιροπίρουνα`,
            `τους αγκώνες και τον προσωπικό χώρο`,
            `τον κεντρικό στολισμό του τραπεζιού`,
            `το να σηκωθεί και να ξανακαθίσει κάποιος`,
          ],
        },
        `Ένα τραπέζι που χωράει δέκα άτομα μπορεί να είναι εντελώς διαφορετική εμπειρία με οκτώ, ειδικά σε ένα δείπνο που κρατάει ώρες.`,
        `Και επειδή στη δεξίωση οι καλεσμένοι μένουν στο τραπέζι τους μεγάλο μέρος της βραδιάς, η άνεση συνήθως αξίζει περισσότερο από το να στριμωχτεί ένας ακόμη.`,
      ],
    },
    {
      heading: "3. Πόσοι κάθονται στις συνηθισμένες ροτόντες;",
      content: [
        `Οι ροτόντες είναι η πιο συνηθισμένη επιλογή στις ελληνικές δεξιώσεις, γιατί βοηθούν τη συζήτηση και γεμίζουν την αίθουσα διαφορετικά από τα μακριά τραπέζια.`,
        `Ως αφετηρία και μόνο, τα συνηθισμένα μεγέθη δουλεύουν περίπου έτσι:`,
        {
          type: "list",
          items: [
            `Ροτόντα 120 εκ. — άνετη αφετηρία: 4 έως 6 άτομα. Πιθανό μέγιστο: 6 έως 8 άτομα.`,
            `Ροτόντα 150 εκ. — άνετη αφετηρία: γύρω στα 8 άτομα. Πιθανό μέγιστο: γύρω στα 10 άτομα.`,
            `Ροτόντα 180 εκ. — άνετη αφετηρία: γύρω στα 10 άτομα. Πιθανό μέγιστο: γύρω στα 12 άτομα.`,
          ],
        },
        `Αυτά είναι νούμερα για να ξεκινήσετε, όχι κανόνες που ισχύουν παντού.`,
        `Η πραγματική χωρητικότητα εξαρτάται από τα συγκεκριμένα τραπέζια και τις καρέκλες του χώρου σας και από το πώς θα στηθεί η δεξίωση. Αν ο χώρος προτείνει διαφορετικό αριθμό, η δική του οδηγία υπερισχύει.`,
        `Η διάκριση ανάμεσα στην άνετη αφετηρία και στο μέγιστο έχει σημασία. Δύο ροτόντες με την ίδια διάμετρο μπορεί να συμπεριφέρονται τελείως αλλιώς, ανάλογα με τις καρέκλες, το σερβίτσιο και τον τρόπο σερβιρίσματος.`,
      ],
    },
    {
      heading: "4. Και τα μακρόστενα τραπέζια;",
      content: [
        `Τα μακρόστενα τραπέζια — και το τραπέζι σε σχήμα Π, που εξακολουθεί να είναι αγαπημένο σε γάμους με πολλούς συγγενείς — θέλουν άλλη προσέγγιση.`,
        `Εκεί μετράει το μήκος της κάθε πλευράς, ενώ οι δύο άκρες μπορεί να χρησιμοποιηθούν ή όχι, ανάλογα με το τραπέζι και τη διάταξη.`,
        `Ένα πιο μακρύ τραπέζι χωράει περισσότερους, χωρίς όμως αυτό να σημαίνει ότι μπορείτε απλώς να διαιρέσετε το μήκος του.`,
        `Δείτε:`,
        {
          type: "list",
          items: [
            `αν θα κάθεται κόσμος και στις δύο άκρες`,
            `το πλάτος του τραπεζιού`,
            `το πλάτος των καρεκλών`,
            `πόσος χώρος μένει πίσω από κάθε καρέκλα`,
            `αν το σερβίτσιο και ο στολισμός τρώνε χρήσιμο χώρο`,
            `πώς κάθεται το τραπέζι μέσα στην αίθουσα`,
          ],
        },
        `Αν ο χώρος σας δουλεύει με μακρόστενα τραπέζια, ζητήστε του τη χωρητικότητα για τη συγκεκριμένη διάταξη που θα στηθεί, αντί να βασιστείτε σε έναν γενικό αριθμό.`,
      ],
    },
    {
      heading: "5. Οι καρέκλες μετράνε περισσότερο απ' όσο νομίζετε",
      content: [
        `Το τραπέζι είναι η μισή ιστορία.`,
        `Μια στενή καρέκλα χωρίς μπράτσα πιάνει εντελώς άλλον χώρο από μια φαρδιά, ταπετσαρισμένη καρέκλα με μπράτσα. Και οι καρέκλες θέλουν χώρο και από πίσω, για να τραβηχτούν και να καθίσει κάποιος χωρίς να ενοχλήσει το διπλανό τραπέζι.`,
        `Αυτό γίνεται ακόμη πιο κρίσιμο όταν τα τραπέζια μπαίνουν κοντά το ένα στο άλλο.`,
        `Αν συζητάτε ήδη τη διάταξη της αίθουσας, κοιτάξτε το σύνολο και όχι μόνο το τραπέζι:`,
        `τραπέζι + καρέκλες + σερβίτσιο + χώρος γύρω από το τραπέζι`,
        `Αυτό είναι που κρίνει τελικά αν μια διάταξη λειτουργεί.`,
      ],
    },
    {
      heading: "6. Το σερβίτσιο και ο στολισμός θέλουν κι αυτά χώρο",
      content: [
        `Δύο τραπέζια των οκτώ ατόμων δεν έχουν πάντα τον ίδιο ωφέλιμο χώρο.`,
        `Ένα λιτό στήσιμο αφήνει εντελώς άλλη αίσθηση από ένα επίσημο σερβίτσιο με πιάτο παρουσίασης, τρία ποτήρια, μαχαιροπίρουνα, μενού, μπομπονιέρες και έναν ψηλό κεντρικό στολισμό.`,
        `Σκεφτείτε τι θα υπάρχει πραγματικά πάνω στο τραπέζι.`,
        `Αν η δεξίωση έχει πλούσιο στολισμό ή πολλά αντικείμενα ανά άτομο, λίγος παραπάνω χώρος στον καθένα αλλάζει αισθητά την αίσθηση του τραπεζιού.`,
        `Είναι άλλος ένας λόγος να μη δείτε τη χωρητικότητα ως σταθερό κανόνα.`,
      ],
    },
    {
      heading: "7. Αλλάζει κάτι ο τρόπος που σερβίρεται το φαγητό;",
      content: [
        `Ναι, αρκετά.`,
        `Σε ένα σερβιριστό δείπνο οι καλεσμένοι περνούν πολλή ώρα καθισμένοι. Όταν τα πιάτα μοιράζονται στη μέση του τραπεζιού, χρειάζεται χώρος για τις πιατέλες. Ο μπουφές ελαφρύνει το τραπέζι, αλλά σημαίνει ότι ο κόσμος θα σηκώνεται συνέχεια.`,
        `Το ερώτημα δεν είναι αν ένας τρόπος σερβιρίσματος επιβάλλει από μόνος του συγκεκριμένο αριθμό θέσεων.`,
        `Είναι:`,
        `Τι θα χρειαστεί να κάνει ο καλεσμένος πάνω στο τραπέζι;`,
        `Όσο περισσότερη ώρα περνάει εκεί τρώγοντας και μιλώντας, τόσο περισσότερο αξίζει η άνεση.`,
      ],
    },
    {
      heading: "8. Πόσα τραπέζια θα χρειαστείτε;",
      content: [
        `Μόλις ξέρετε την άνετη χωρητικότητα των τραπεζιών σας, μπορείτε να υπολογίσετε πόσα χρειάζεστε.`,
        `Για παράδειγμα:`,
        `120 καλεσμένοι στη δεξίωση ÷ 8 άτομα ανά τραπέζι = 15 τραπέζια`,
        `Υπάρχει όμως μια λεπτομέρεια που αλλάζει το αποτέλεσμα:`,
        `Χρησιμοποιήστε τους επιβεβαιωμένους καλεσμένους της δεξίωσης, όχι τη λίστα με την οποία ξεκινήσατε.`,
        `Κάποιοι δεν θα έρθουν. Άλλοι θα είναι στην εκκλησία και όχι στη δεξίωση. Το πλάνο σας πρέπει να στηρίζεται σε αυτούς που όντως θα καθίσουν.`,
        `Γι' αυτό και τα τραπέζια δουλεύονται καλύτερα όταν έχετε αρκετά καθαρή εικόνα των απαντήσεων.`,
        {
          type: "p",
          text: [
            `Το πώς περνάτε από τις απαντήσεις στο τελικό πλάνο είναι το θέμα του οδηγού `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
      ],
    },
    {
      heading: "9. Τι γίνεται αν το τελευταίο τραπέζι δεν γεμίζει;",
      content: [
        `Σπάνια βγαίνουν όλα τα τραπέζια ίσα.`,
        `Φανταστείτε ότι έχετε 86 επιβεβαιωμένους καλεσμένους στη δεξίωση και τραπέζια για οκτώ έως δέκα άτομα. Αν απλώς διαιρέσετε, θα καταλήξετε με ένα μικρότερο τελευταίο τραπέζι.`,
        `Αυτό δεν σημαίνει ότι πρέπει να ανακατέψετε παρέες μόνο και μόνο για να βγουν όλα τα τραπέζια ίδια.`,
        `Δείτε ποιοι μένουν χωρίς θέση και ρωτήστε:`,
        {
          type: "list",
          items: [
            `Ποιοι γνωρίζονται ήδη μεταξύ τους;`,
            `Ποιοι νιώθουν άνετα σε μεγαλύτερη παρέα;`,
            `Υπάρχουν ζευγάρια ή συγγενείς που πρέπει να μείνουν μαζί;`,
            `Ποιοι θα κέρδιζαν από το να καθίσουν με γνωστά πρόσωπα;`,
            `Υπάρχουν συνδυασμοί που καλύτερα να αποφύγετε;`,
          ],
        },
        `Ένα τραπέζι με λιγότερα άτομα είναι μια χαρά, αν οι άνθρωποι που κάθονται εκεί ταιριάζουν μεταξύ τους.`,
      ],
    },
    {
      heading: "10. Μην επιλέγετε τραπέζι χωρίς να δείτε την αίθουσα",
      content: [
        `Η χωρητικότητα του τραπεζιού είναι μόνο ένα κομμάτι της διάταξης.`,
        `Το τελικό στήσιμο εξαρτάται και από την ίδια την αίθουσα.`,
        `Ένα τραπέζι που παίρνει άνετα δέκα άτομα μπορεί να είναι λάθος επιλογή, αν για να χωρέσουν όλα τα τραπέζια πρέπει να μπουν το ένα κολλητά στο άλλο.`,
        `Δείτε ολόκληρη την εικόνα:`,
        `μέγεθος τραπεζιού → άτομα ανά τραπέζι → διάταξη αίθουσας → σερβίρισμα → κίνηση`,
        `Οι καλεσμένοι πρέπει να μπορούν να καθίσουν, να σηκωθούν, να πάνε από τραπέζι σε τραπέζι και να ζήσουν όλη τη βραδιά χωρίς να στριμώχνονται.`,
        `Ο χώρος ή ο wedding planner σας μπορεί να σας επιβεβαιώσει τη ρεαλιστική διάταξη για τη συγκεκριμένη αίθουσα.`,
      ],
    },
    {
      heading: "11. Ένας πρακτικός κανόνας",
      content: [
        `Αν χρειάζεστε αφετηρία, τα 8 με 10 άτομα ανά ροτόντα δουλεύουν σε πολλές δεξιώσεις.`,
        `Μη σταματήσετε όμως στον αριθμό.`,
        `Ξεκινήστε από την άνετη χωρητικότητα που προτείνει ο χώρος για τα δικά του τραπέζια και προσαρμόστε το πλάνο ανάλογα με:`,
        {
          type: "list",
          items: [
            `το μέγεθος και το σχήμα των τραπεζιών`,
            `το μέγεθος των καρεκλών`,
            `το σερβίτσιο`,
            `τον στολισμό`,
            `τον τρόπο σερβιρίσματος`,
            `τη διάταξη της αίθουσας`,
            `τις παρέες που πρέπει να καθίσουν κάπου`,
          ],
        },
        `Ζητούμενο δεν είναι να χωρέσουν όσο το δυνατόν περισσότεροι γύρω από κάθε τραπέζι.`,
        `Ζητούμενο είναι τραπέζια όπου οι άνθρωποι κάθονται άνετα και περνούν καλά μαζί.`,
      ],
    },
    {
      heading: "12. Από τη χωρητικότητα στο πλάνο τραπεζιών",
      content: [
        `Το ότι ένα τραπέζι παίρνει οκτώ άτομα είναι μόνο η αρχή.`,
        `Το δύσκολο ερώτημα είναι:`,
        `Ποιοι οκτώ;`,
        `Αφού ξέρετε τη χωρητικότητα, μένει να δείτε παρέες, να αποφασίσετε ποιοι κάθονται μαζί, να βάλετε ονόματα στα τραπέζια και να ξαναδείτε το σύνολο για ό,τι δεν στέκει.`,
        `Η διαδρομή δηλαδή μοιάζει κάπως έτσι:`,
        `επιβεβαιωμένοι καλεσμένοι δεξίωσης → χωρητικότητα τραπεζιών → παρέες → θέσεις → έλεγχος`,
        `Εκεί το πλάνο παύει να είναι αριθμητική και γίνεται απόφαση.`,
        {
          type: "p",
          text: [
            `Για τη διαδικασία βήμα βήμα, διαβάστε το `,
            {
              text: "Πλάνο τραπεζιών γάμου: πώς να το φτιάξετε βήμα βήμα",
              href: "/guides/wedding-seating-plan",
            },
            `.`,
          ],
        },
        {
          type: "note",
          text: [
            `Αν οργανώνετε τον γάμο σας με το Agapaé, τα εργαλεία για τα τραπέζια είναι διαθέσιμα στο πακέτο Heirloom. Ο οδηγός παραπάνω εξηγεί τη διαδικασία· η σελίδα `,
            { text: "Τραπέζια γάμου", href: "/wedding-seating" },
            ` δείχνει πώς δουλεύουν τα τραπέζια μέσα στην πλατφόρμα.`,
          ],
        },
      ],
    },
  ],

  faqHeading: "Συχνές ερωτήσεις",

  faq: [
    {
      q: `Πόσοι καλεσμένοι κάθονται σε μια ροτόντα;`,
      a: `Στις περισσότερες δεξιώσεις τα 8 με 10 άτομα είναι μια χρήσιμη αφετηρία. Ο σωστός αριθμός εξαρτάται από τη διάμετρο του τραπεζιού, τις καρέκλες, το σερβίτσιο, τον στολισμό και τη χωρητικότητα που προτείνει ο χώρος σας.`,
    },
    {
      q: `Είναι πολλά τα δέκα άτομα σε ένα τραπέζι;`,
      a: `Όχι απαραίτητα. Δέκα άτομα κάθονται άνετα σε αρκετά μεγάλο τραπέζι με σωστό στήσιμο. Το αν λειτουργεί εξαρτάται από τις διαστάσεις, τις καρέκλες, το σερβίτσιο και τον χώρο που μένει γύρω από το τραπέζι.`,
    },
    {
      q: `Πόσοι χωρούν σε ροτόντα 150 εκ.;`,
      a: `Μια ροτόντα 150 εκ. υπολογίζεται συνήθως για οκτώ άτομα, με δέκα να είναι εφικτά ανάλογα με τις καρέκλες και το στήσιμο της δεξίωσης. Η οδηγία του χώρου σας υπερισχύει.`,
    },
    {
      q: `Πόσοι χωρούν σε ροτόντα 180 εκ.;`,
      a: `Μια ροτόντα 180 εκ. παίρνει συνήθως γύρω στα δέκα άτομα με άνεση, και περισσότερα ανάλογα με το στήσιμο. Επιβεβαιώστε τον αριθμό με τον χώρο ή την εταιρεία ενοικίασης.`,
    },
    {
      q: `Πρέπει όλα τα τραπέζια να έχουν τον ίδιο αριθμό ατόμων;`,
      a: `Όχι. Τα τραπέζια δεν χρειάζεται να είναι ίσα. Ένα τραπέζι με λιγότερα άτομα δουλεύει μια χαρά όταν η παρέα ταιριάζει, ενώ η προσπάθεια να βγουν όλα ίδια οδηγεί συχνά σε πιο αφύσικους συνδυασμούς.`,
    },
  ],

  related: {
    heading: "Πού ταιριάζει αυτό στο Agapaé",

    // Guides with a Greek version, in the same seating cluster. The registry
    // refuses a slug it does not know, and the test refuses one with no Greek
    // module — a Greek article must not send its reader to an English one.
    guides: ["wedding-seating-plan", "wedding-seating-chart-vs-seating-plan"],

    product: [
      {
        href: "/wedding-seating",
        label: "Τραπέζια γάμου",
        blurb:
          "Δείτε πώς δουλεύουν τα τραπέζια μέσα στο Agapaé. Τα εργαλεία για τα τραπέζια είναι διαθέσιμα στο πακέτο Heirloom.",
      },
    ],
  },
};

export default howManyPeopleWeddingTableEl;
